import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Linkedin, Facebook, Youtube, Instagram, Send, User, Building, MessageSquare, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import { formatPhoneNumber, formatPhoneForCRM, isValidPhoneNumber } from '../utils/phoneFormatter';
import { submitToBlackOps, captureAttribution } from '../utils/leadCapture';
import TurnstileWidget from '../components/TurnstileWidget';
import { useTurnstile } from '../hooks/useTurnstile';
import { TURNSTILE_REQUIRED_MESSAGE } from '../utils/turnstile';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const turnstile = useTurnstile();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate required fields
    if (!formData.firstName.trim()) {
      setError('Please enter your first name');
      setIsSubmitting(false);
      return;
    }

    if (!formData.lastName.trim()) {
      setError('Please enter your last name');
      setIsSubmitting(false);
      return;
    }

    if (!formData.email.trim()) {
      setError('Please enter your email address');
      setIsSubmitting(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      setIsSubmitting(false);
      return;
    }

    // Validate phone number if provided
    if (formData.phone && !isValidPhoneNumber(formData.phone)) {
      setPhoneError('Please enter a valid phone number');
      setIsSubmitting(false);
      return;
    }

    if (!turnstile.token) {
      setError(TURNSTILE_REQUIRED_MESSAGE);
      setIsSubmitting(false);
      return;
    }

    try {
      // Format phone number for CRM
      const formattedPhone = formData.phone ? formatPhoneForCRM(formData.phone) : '';

      const payload = {
        form_type: 'contact' as const,
        form_id: 'contact_page_main',
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim(),
        company_name: formData.company.trim() || undefined,
        phone: formattedPhone || undefined,
        message: formData.message.trim() || undefined,
        product_interest: 'General Inquiry',
        source: 'Contact Page Form',
        turnstile_token: turnstile.token,
        ...captureAttribution(),
      };

      const result = await submitToBlackOps(payload);

      if (result.success) {
        setIsSuccess(true);

        // Reset form after short delay
        setTimeout(() => {
          setFormData({ firstName: '', lastName: '', company: '', email: '', phone: '', message: '' });
          setPhoneError('');
          setIsSuccess(false);
        }, 5000);
      } else {
        console.error('Lead capture error:', result.error);
        setError('Something went wrong, please try again or call us at (509) 901-7476.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Something went wrong, please try again or call us at (509) 901-7476.');
    }

    // Turnstile tokens are single-use — clear it so a second submission works.
    turnstile.reset();
    setIsSubmitting(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Clear errors when user starts typing
    if (error) setError('');
    
    if (name === 'phone') {
      // Format phone number as user types
      const formattedValue = formatPhoneNumber(value);
      setFormData({
        ...formData,
        [name]: formattedValue
      });
      
      // Clear phone error when user starts typing
      if (phoneError) {
        setPhoneError('');
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Contact Us"
        description="Ready to improve your agricultural operations? Contact our experts for personalized recommendations and competitive pricing."
        canonicalPath="/contact"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-50 via-ctc-silver-light to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center space-x-2 bg-ctc-blue/10 text-ctc-blue px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Phone size={16} />
              <span>Get In Touch</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Ready to improve your agricultural operations? Contact our experts for 
              personalized recommendations and competitive pricing.
            </p>
            
            {/* Social Media Links in Hero */}
            <div className="flex justify-center space-x-6">
              <a
                href="https://www.linkedin.com/company/ctc-waterworks/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-ctc-blue/20 rounded-lg flex items-center justify-center hover:bg-ctc-blue hover:scale-110 transition-all duration-300 group"
                aria-label="Follow CTC Waterworks on LinkedIn"
              >
                <Linkedin className="text-ctc-blue group-hover:text-white" size={24} />
              </a>
              <a
                href="https://www.facebook.com/profile.php?viewas=100000686899395&id=61577642326281"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center hover:bg-blue-600 hover:scale-110 transition-all duration-300 group"
                aria-label="Follow CTC Waterworks on Facebook"
              >
                <Facebook className="text-blue-400 group-hover:text-white" size={24} />
              </a>
              <a
                href="https://www.youtube.com/@CTCWaterworks"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center hover:bg-red-600 hover:scale-110 transition-all duration-300 group"
                aria-label="Subscribe to CTC Waterworks on YouTube"
              >
                <Youtube className="text-red-400 group-hover:text-white" size={24} />
              </a>
              <a
                href="https://www.instagram.com/ctcwaterworks"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-pink-600/20 rounded-lg flex items-center justify-center hover:bg-pink-600 hover:scale-110 transition-all duration-300 group"
                aria-label="Follow CTC Waterworks on Instagram"
              >
                <Instagram className="text-pink-400 group-hover:text-white" size={24} />
              </a>
              <a
                href="https://g.page/r/CaOodiIX-wQsEBM/review"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center hover:bg-yellow-500 hover:scale-110 transition-all duration-300 group"
                aria-label="Review CTC Waterworks on Google"
              >
                <Star className="text-yellow-500 group-hover:text-white" size={24} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              {isSuccess ? (
                <div className="bg-green-50 rounded-2xl p-8 border border-green-200">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Send className="text-green-600" size={32} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Thank You!</h2>
                    <p className="text-gray-600 mb-6">
                      Your message has been submitted successfully. Our team will contact you within 24 hours.
                    </p>
                    <div className="bg-green-100 rounded-lg p-4">
                      <p className="text-green-800 font-medium">Need immediate assistance?</p>
                      <p className="text-green-700 text-xl font-bold">(509) 901-7476</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-700 text-sm">{error}</p>
                      </div>
                    )}

                    <div>
                      <label htmlFor="contact-firstName" className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="text"
                          id="contact-firstName"
                          name="firstName"
                          required
                          value={formData.firstName}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                          placeholder="Enter your first name"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-lastName" className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="text"
                          id="contact-lastName"
                          name="lastName"
                          required
                          value={formData.lastName}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                          placeholder="Enter your last name"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-company" className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name
                      </label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="text"
                          id="contact-company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                          placeholder="Enter your company name"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                          placeholder="Enter your email address"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="tel"
                          id="contact-phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors ${
                            phoneError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                          placeholder="(555) 123-4567"
                          maxLength={14}
                        />
                      </div>
                      {phoneError && (
                        <p className="mt-1 text-sm text-red-600">{phoneError}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">
                        Message
                      </label>
                      <div className="relative">
                        <MessageSquare className="absolute left-3 top-3 text-gray-400" size={20} />
                        <textarea
                          id="contact-message"
                          name="message"
                          rows={5}
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your project, operation, or questions..."
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors resize-none"
                        />
                      </div>
                    </div>

                    <TurnstileWidget
                      ref={turnstile.ref}
                      onToken={turnstile.setToken}
                      action="contact_page_main"
                    />

                    <motion.button
                      type="submit"
                      disabled={isSubmitting || !formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white px-8 py-4 rounded-lg font-semibold hover:from-ctc-blue-dark hover:to-ctc-blue transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          <span>Send Message</span>
                        </>
                      )}
                    </motion.button>
                  </form>
                </div>
              )}
            </motion.div>

            {/* Contact Information & Map */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              {/* Contact Information */}
              <div className="bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-2xl p-8 border border-ctc-blue/20">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-ctc-blue-light to-ctc-blue rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                      <Phone className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">Phone Numbers</h4>
                      <p className="text-gray-600">Headquarters: (509) 965-3333</p>
                      <p className="text-gray-600">Andre: (509) 901-7476</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-ctc-blue-light to-ctc-blue rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                      <Mail className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">Email Addresses</h4>
                      <p className="text-gray-600">Sales@CTCWaterworks.com</p>
                      <p className="text-gray-600">Andre@CTCWaterworks.com</p>
                      <p className="text-gray-600">Brandon@CTCWaterworks.com</p>
                      <p className="text-gray-600">Accounting@CTCWaterworks.com</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-ctc-blue-light to-ctc-blue rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                      <MapPin className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">Address</h4>
                      <a
                        href="https://maps.google.com/?q=460+McLaughlin+Rd,+Yakima,+WA+98908"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-ctc-blue transition-colors"
                      >
                        460 McLaughlin Rd.<br />
                        Yakima, WA 98908
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-ctc-blue-light to-ctc-blue rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                      <Clock className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">Business Hours</h4>
                      <p className="text-gray-600">Monday - Thursday: 6:30 AM - 4:30 PM PST</p>
                      <p className="text-gray-600">Friday & Saturday: By appointment</p>
                      <p className="text-gray-600">Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Team Profiles */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Connect with Our Team</h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center flex flex-col h-full">
                    <Link to="/team/brandon" className="block group mb-4">
                      <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-4 border-4 border-ctc-blue/20 shadow-lg group-hover:border-ctc-blue transition-all">
                        <img
                          src="/Brandon Haley.png"
                          alt="Brandon Haley"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h4 className="font-semibold text-gray-900 group-hover:text-ctc-blue transition-colors">Brandon Haley</h4>
                      <p className="text-gray-600 text-sm">CEO</p>
                    </Link>
                    <div className="mt-auto">
                      <a
                        href="https://www.linkedin.com/in/brandon-haley-154b0033/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 text-ctc-blue hover:text-ctc-blue-dark transition-colors"
                      >
                        <Linkedin size={16} />
                        <span className="text-sm">LinkedIn</span>
                      </a>
                    </div>
                  </div>

                  <div className="text-center flex flex-col h-full">
                    <Link to="/team/andre" className="block group mb-4">
                      <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-4 border-4 border-green-200 shadow-lg group-hover:border-green-600 transition-all">
                        <img
                          src="/Andre Savala.png"
                          alt="Andre Savala"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h4 className="font-semibold text-gray-900 group-hover:text-green-600 transition-colors">Andre Savala</h4>
                      <p className="text-gray-600 text-sm">Sales & Customer Support</p>
                    </Link>
                    <div className="mt-auto">
                      <a
                        href="https://www.linkedin.com/in/andre-savala-180374157/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 text-green-600 hover:text-green-700 transition-colors"
                      >
                        <Linkedin size={16} />
                        <span className="text-sm">LinkedIn</span>
                      </a>
                    </div>
                  </div>

                  <div className="text-center flex flex-col h-full">
                    <Link to="/team/jordan" className="block group mb-4">
                      <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-4 border-4 border-pink-200 shadow-lg group-hover:border-pink-600 transition-all">
                        <img
                          src="/Jordan Sloan.png"
                          alt="Jordan Sloan"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h4 className="font-semibold text-gray-900 group-hover:text-pink-600 transition-colors">Jordan Sloan</h4>
                      <p className="text-gray-600 text-sm">Customer Experience & Outreach</p>
                    </Link>
                    <div className="mt-auto">
                      <a
                        href="https://www.linkedin.com/in/jordan-sloan-275a4829b/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 text-pink-600 hover:text-pink-700 transition-colors"
                      >
                        <Linkedin size={16} />
                        <span className="text-sm">LinkedIn</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Google Map */}
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                <div className="h-64">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2823.8234567890123!2d-120.5234567!3d46.6234567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x549691c8171fa8ab%3A0x2c0bf8a1f2a8a0!2s460%20McLaughlin%20Rd%2C%20Yakima%2C%20WA%2098908!5e0!3m2!1sen!2sus!4v1234567890123"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                    title="CTC Waterworks Location"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;